import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginOAuthParams,
  OauthAEMConfig,
  PkceCode,
  SwapAuthForAccessTokensParams
} from '../../models';
import { AuthorizationService } from '../authorization-service/authorization-service';
import { PostWithCredService } from '../remember-me-adapter-service/post-with-cred/post-with-cred-service';
import { SwapAccessTokenService } from '../swap-access-token-service/swap-access-token-service';
import { UtilMethodsHelperService } from '../util-service/util-methods-helper.service';
import { UtilService } from '../util-service/util-service';
import { TokenHelperService } from './token-helper.service';

@Injectable({
  providedIn: 'root'
})
export class TokenService extends TokenHelperService {
  authConfig!: OauthAEMConfig;
  pkceCode!: PkceCode;
  headers!: HttpHeaders;

  constructor(
    protected override utils: UtilService,
    protected override utilMethodsHelper: UtilMethodsHelperService,
    private authorizationService: AuthorizationService,
    protected override postWithCred: PostWithCredService,
    protected override swapAuthForAccessTokenService: SwapAccessTokenService,
    protected override logging: LoggingService
  ) {
    super(
      utils,
      utilMethodsHelper,
      postWithCred,
      swapAuthForAccessTokenService,
      logging
    );
  }

  async getAccessTokenWithRequestInput(
    config: OauthAEMConfig,
    requestInput: ILoginUserNamePasswordInput
  ) {
    const pkceCode = this.utils.generateCodeChallenge();
    const loginOAuthParams: LoginOAuthParams = {
      oauthConfig: config,
      pkceCode: pkceCode
    };

    this.loginOAuth = await this.authorizationService.loginOAuth(
      loginOAuthParams
    );

    if (!(this.loginOAuth.status === config.COMPLETED)) {
      await this.loginOauthHelper(config, requestInput);
    }

    if (this.isCompletedStatus(config)) {
      return await this.loginAuthWithCredentialsHelper(config, pkceCode);
    } else if (this.utils.isAuthErrorResponse(this.loginAuthWithCredentials)) {
      const errorId = this.utils.populatePingErrorResponse(
        this.loginAuthWithCredentials
      );
      return {
        ...this.loginAuthWithCredentials,
        errorId: errorId
      };
    } else {
      return this.logging.log('invalid response for loginAuthWithCredentials');
    }
  }

  async loginOAuthCall(config: OauthAEMConfig, overrideScope?: string) {
    let loginOAuthParams: LoginOAuthParams = {
      oauthConfig: config,
      pkceCode: this.pkceCode
    };

    if (overrideScope) {
      const mfaLoginOAuthParams = JSON.parse(JSON.stringify(loginOAuthParams));
      mfaLoginOAuthParams.oauthConfig.SCOPE_VALUE_RM = overrideScope
        ? overrideScope
        : mfaLoginOAuthParams.oauthConfig.SCOPE_VALUE_RM;
      loginOAuthParams = mfaLoginOAuthParams;
    }

    this.loginOAuth = await this.authorizationService.loginOAuth(
      loginOAuthParams
    );
  }

  async getAccessToken(
    config: OauthAEMConfig,
    overrideScope?: string
  ): Promise<AuthAccessTokenDetails> {
    let tokenDetails = {} as AuthAccessTokenDetails;
    this.pkceCode = this.utils.generateCodeChallenge();

    await this.loginOAuthCall(config, overrideScope);

    if (this.loginOAuth && this.loginOAuth.status === config.COMPLETED) {
      const swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams = {
        authorizationCode: this.loginOAuth?.authorizeResponse?.code,
        oauthConfig: config,
        pkcCodeGenerated: this.pkceCode
      };
      this.swapAuthForAccessTokens =
        await this.swapAuthForAccessTokenService.swapAuthForAccessTokens(
          swapAuthForAccessTokensParams
        );
    }

    if (this.swapAuthForAccessTokens?.access_token) {
      const currentTime = new Date().getTime(); /** for the token issued time */
      tokenDetails = {
        access_token: this.swapAuthForAccessTokens.access_token,
        expires_in: this.swapAuthForAccessTokens?.expires_in,
        token_issued_time: currentTime
      };
    }
    return tokenDetails;
  }
}
